import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { API_BASE_URL, getEventById } from "../../services/api";
import "../styles/EventDetailPage.css";
import { isEventActive } from "../../utils/eventDates";
import { useLanguage } from "../../contexts/LanguageContext";

const translations = {
  kz: {
    backToEvents: "Оқиғалар тізіміне оралу",
    period: "Мерзімі:",
    location: "Өтетін орны:",
    description: "Сипаттама",
    registration: "Тіркелу",
    interested:
      "Бұл оқиғаға қызығушылық таныттыңыз ба? Ұйымдастырушылармен байланысыңыз.",
    learnMore: "Толығырақ білу",
    loading: "Іс-шара туралы ақпарат жүктелуде...",
    errorTitle: "Қате пайда болды",
    notFound: "Іс-шара табылмады",
    notFoundDescription: "Сұралған іс-шара жоқ немесе жойылған.",
  },
  ru: {
    backToEvents: "Вернуться к списку событий",
    period: "Период:",
    location: "Место проведения:",
    description: "Описание",
    registration: "Регистрация",
    interested:
      "Заинтересовались этим событием? Свяжитесь с организаторами для получения дополнительной информации.",
    learnMore: "Узнать подробнее",
    loading: "Загрузка информации о событии...",
    errorTitle: "Произошла ошибка",
    notFound: "Событие не найдено",
    notFoundDescription:
      "Запрашиваемое событие не существует или было удалено.",
  },
  en: {
    backToEvents: "Back to Events List",
    period: "Period:",
    location: "Location:",
    description: "Description",
    registration: "Registration",
    interested:
      "Interested in this event? Contact the organizers for more information.",
    learnMore: "Learn More",
    loading: "Loading event information...",
    errorTitle: "An error occurred",
    notFound: "Event not found",
    notFoundDescription:
      "The requested event does not exist or has been deleted.",
  },
};

const getMonthName = (isoDate, language = "en") => {
  const date = new Date(isoDate);
  const month = date.getMonth();

  const monthNames = {
    en: [
      "JAN",
      "FEB",
      "MAR",
      "APR",
      "MAY",
      "JUN",
      "JUL",
      "AUG",
      "SEP",
      "OCT",
      "NOV",
      "DEC",
    ],
    ru: [
      "ЯНВ",
      "ФЕВ",
      "МАР",
      "АПР",
      "МАЙ",
      "ИЮН",
      "ИЮЛ",
      "АВГ",
      "СЕН",
      "ОКТ",
      "НОЯ",
      "ДЕК",
    ],
    kz: [
      "ҚАҢ",
      "АҚП",
      "НАУ",
      "СӘУ",
      "МАМ",
      "МАУ",
      "ШІЛ",
      "ТАМ",
      "ҚЫР",
      "ҚАЗ",
      "ҚАР",
      "ЖЕЛ",
    ],
  };

  return monthNames[language]
    ? monthNames[language][month]
    : monthNames.en[month];
};

const getWeekdayName = (isoDate, language = "en") => {
  const date = new Date(isoDate);
  const day = date.getDay();

  const weekdayNames = {
    en: ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"],
    ru: ["ВС", "ПН", "ВТ", "СР", "ЧТ", "ПТ", "СБ"],
    kz: ["ЖС", "ДС", "СС", "СР", "БС", "ЖМ", "СБ"],
  };

  return weekdayNames[language]
    ? weekdayNames[language][day]
    : weekdayNames.en[day];
};

const getDayOfMonth = (isoDate) => {
  const date = new Date(isoDate);
  return String(date.getDate()).padStart(2, "0");
};

const formatDate = (isoDate) => {
  const date = new Date(isoDate);
  return date.toLocaleDateString("ru-RU");
};

const formatTime = (isoDate) => {
  const date = new Date(isoDate);
  return date.toLocaleTimeString("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatEventPeriod = (event) => {
  if (event.date) {
    const parts = event.date.split("–").map(s => s.trim());
    if (parts.length === 2 && parts[0] === parts[1]) return parts[0];
    return event.date;
  }
  const fmt = (d) => {
    if (!d) return "";
    const [y, m, day] = d.split("-");
    return `${day}.${m}.${y}`;
  };
  if (event.startDate && event.endDate) {
    if (event.startDate === event.endDate) {
      return fmt(event.startDate);
    }
    return `${fmt(event.startDate)} – ${fmt(event.endDate)}`;
  }
  if (event.startDate) return fmt(event.startDate);
  if (event.endDate) return fmt(event.endDate);
  return "";
};

function EventDetailPage() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage] || translations.kz;

  const getTranslation = (translationsArray) => {
    if (!translationsArray || translationsArray.length === 0) return "";

    const current = translationsArray.find(
      (t) => t.languageCode === currentLanguage
    );
    if (current?.textValue) return current.textValue;

    const kz = translationsArray.find((t) => t.languageCode === "kz");
    if (kz?.textValue) return kz.textValue;

    const ru = translationsArray.find((t) => t.languageCode === "ru");
    if (ru?.textValue) return ru.textValue;

    const en = translationsArray.find((t) => t.languageCode === "en");
    if (en?.textValue) return en.textValue;

    return "";
  };

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const data = await getEventById(id);
        setEvent(data.event);
      } catch {
        setError("Error loading event");
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [id]);

  if (loading) {
    return (
      <div className="event-detail-loading">
        <div className="loading-spinner"></div>
        <p>{t.loading}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="event-detail-error">
        <h2>{t.errorTitle}</h2>
        <p>{error}</p>
        <Link to="/events" className="back-to-events-btn">
          {t.backToEvents}
        </Link>
      </div>
    );
  }

  if (!event || !isEventActive(event)) {
    return (
      <div className="event-detail-error">
        <h2>{t.notFound}</h2>
        <p>{t.notFoundDescription}</p>
        <Link to="/events" className="back-to-events-btn">
          {t.backToEvents}
        </Link>
      </div>
    );
  }

  const eventName = getTranslation(event.eventName?.translations);
  const description = getTranslation(event.eventDescription?.translations);
  const place = getTranslation(event.eventPlace?.translations);

  if (!eventName && !description) {
    return (
      <div className="event-detail-error">
        <h2>{t.notFound}</h2>
        <p>{t.notFoundDescription}</p>
        <Link to="/events" className="back-to-events-btn">
          {t.backToEvents}
        </Link>
      </div>
    );
  }

  const periodStr = formatEventPeriod(event);

  return (
    <div className="event-detail-page">
      <div className="event-detail-header">
        <Link to="/events" className="back-button">
          <span className="back-icon"></span>
          {t.backToEvents}
        </Link>
      </div>

      <div className="event-detail-container">
        <div className="event-detail-card">
          {event.startDate && (
            <div className="event-detail-date-badge">
              <div className="event-detail-calendar">
                <div className="calendar-month">
                  {getMonthName(event.startDate, currentLanguage)}
                </div>
                <div className="calendar-day">{getDayOfMonth(event.startDate)}</div>
                <div className="calendar-weekday">
                  {getWeekdayName(event.startDate, currentLanguage)}
                </div>
              </div>
            </div>
          )}

          {event.img_url && (
            <div className="event-detail-image-wrapper">
              <img
                className="event-detail-image"
                src={API_BASE_URL + event.img_url}
                alt={eventName}
              />
            </div>
          )}

          <h1 className="event-detail-title">{eventName}</h1>

          <div className="event-detail-meta">
            {periodStr && (
              <div className="event-detail-meta-item">
                <div className="event-detail-icon date-icon"></div>
                <span className="meta-label">{t.period}</span>
                <span className="meta-value">{periodStr}</span>
              </div>
            )}

            {place && (
              <div className="event-detail-meta-item">
                <div className="event-detail-icon location-icon"></div>
                <span className="meta-label">{t.location}</span>
                <span className="meta-value">{place}</span>
              </div>
            )}
          </div>

          <div className="event-detail-divider"></div>

          <div className="event-detail-description">
            <h2 className="detail-section-title">{t.description}</h2>
            <div className="event-detail-description-content">
              {description}
            </div>
          </div>

          <div className="event-detail-registration">
            <p>{t.interested}</p>
            <a href="/questions" className="event-registration-btn">
              {t.learnMore}
              <span className="registration-icon"></span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EventDetailPage;